import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  CompanySepaDebitInfo,
  CreateExternalBankAccount,
  CreateMemobankMandate,
  GetMemobankMandateResponse,
  MemobankMandate,
} from "./sepaDebitApi";

export interface SepaDebitState {
  createExternalBankAccountStatus: QueryStatus;
  getCompanyByAccountProviderIdStatus: QueryStatus;
  companySepaDebitInfo: CompanySepaDebitInfo;
  isStripeConnectMandateSigned: boolean | null;
  isStripePlatformMandateSigned: boolean | null;
  createMemobankMandateStatus: QueryStatus;
  isMemobankMandateSent: boolean | null;
  getMemobankMandatePerCompanyStatus: QueryStatus;
  companyMemobankMandate: MemobankMandate | null;
}

const initialState: SepaDebitState = {
  createExternalBankAccountStatus: "idle",
  getCompanyByAccountProviderIdStatus: "idle",
  createMemobankMandateStatus: "idle",
  getMemobankMandatePerCompanyStatus: "idle",
  companySepaDebitInfo: {
    connectMandateStatus: "PENDING_SIGNATURE",
    connectClientSecret: "",
    connectSetupIntent: "",
    platformMandateStatus: "PENDING_SIGNATURE",
    platformClientSecret: "",
    platformSetupIntent: "",
  },
  isStripeConnectMandateSigned: null,
  isStripePlatformMandateSigned: null,
  isMemobankMandateSent: null,
  companyMemobankMandate: null,
};

export const createExternalBankAccountAsync = createAsyncThunk(
  "sepa_debit/external_bank_account",
  async (payload: CreateExternalBankAccount, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/sepa_debit/external_bank_account`, payload);
  },
);

export const createMemobankMandateAsync = createAsyncThunk(
  "sepa_debit/memobank_mandate",
  async (payload: CreateMemobankMandate, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/sepa_debit/memobank_mandate`, payload);
  },
);

export const getMemobankMandatePerCompanyAsync = createAsyncThunk(
  "sepa_debit/memobank_mandate/company/:companyUuid",
  async (payload: { companyUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<GetMemobankMandateResponse>(
      `/sepa_debit/memobank_mandate/company/${payload.companyUuid}`,
    );
    return response.data.memobankMandate;
  },
);

export const getCompanySepaDebitInfoByAccountProviderIdAsync = createAsyncThunk(
  "sepa_debit/company",
  async (payload: { accountProviderId: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<CompanySepaDebitInfo>(
      `/sepa_debit/company_provider_id/${payload.accountProviderId}`,
    );
    return response.data;
  },
);

export const sepaDebitSlice = createSlice({
  name: "sepaDebit",
  initialState,
  reducers: {
    resetExternalBankAccountStatus(state) {
      state.createExternalBankAccountStatus = "idle";
    },
    resetCreateMemobankMandateStatus(state) {
      state.createMemobankMandateStatus = "idle";
    },
    setConnectSepaMandateSigned(state, action: PayloadAction<boolean | null>) {
      state.isStripeConnectMandateSigned = action.payload;
    },
    setPlatformSepaMandateSigned(state, action: PayloadAction<boolean | null>) {
      state.isStripePlatformMandateSigned = action.payload;
    },
    setMemobankMandateSent(state, action: PayloadAction<boolean | null>) {
      state.isMemobankMandateSent = action.payload;
    },
    resetGetMemobankMandatePerCompanyStatus(state) {
      state.getMemobankMandatePerCompanyStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createExternalBankAccountAsync.pending, (state) => {
        state.createExternalBankAccountStatus = "processing";
      })
      .addCase(createExternalBankAccountAsync.fulfilled, (state) => {
        state.createExternalBankAccountStatus = "success";
      })
      .addCase(createExternalBankAccountAsync.rejected, (state) => {
        state.createExternalBankAccountStatus = "failed";
      })
      .addCase(createMemobankMandateAsync.pending, (state) => {
        state.createMemobankMandateStatus = "processing";
      })
      .addCase(createMemobankMandateAsync.fulfilled, (state) => {
        state.createMemobankMandateStatus = "success";
      })
      .addCase(createMemobankMandateAsync.rejected, (state) => {
        state.createMemobankMandateStatus = "failed";
      })
      .addCase(getMemobankMandatePerCompanyAsync.pending, (state) => {
        state.getMemobankMandatePerCompanyStatus = "processing";
      })
      .addCase(getMemobankMandatePerCompanyAsync.fulfilled, (state, action) => {
        state.companyMemobankMandate = action.payload;
        state.getMemobankMandatePerCompanyStatus = "success";
      })
      .addCase(getMemobankMandatePerCompanyAsync.rejected, (state) => {
        state.getMemobankMandatePerCompanyStatus = "failed";
      })
      .addCase(
        getCompanySepaDebitInfoByAccountProviderIdAsync.pending,
        (state) => {
          state.getCompanyByAccountProviderIdStatus = "processing";
        },
      )
      .addCase(
        getCompanySepaDebitInfoByAccountProviderIdAsync.fulfilled,
        (state, action) => {
          state.getCompanyByAccountProviderIdStatus = "success";
          state.companySepaDebitInfo = action.payload;
        },
      )
      .addCase(
        getCompanySepaDebitInfoByAccountProviderIdAsync.rejected,
        (state) => {
          state.getCompanyByAccountProviderIdStatus = "failed";
        },
      );
  },
});

export const selectCreateExternalBankAccountStatus = (state: RootState) =>
  state.sepaDebit.createExternalBankAccountStatus;

export const selectCreateMemobankMandateStatus = (state: RootState) =>
  state.sepaDebit.createMemobankMandateStatus;

export const selectGetMemobankMandatePerCompanyStatus = (state: RootState) =>
  state.sepaDebit.getMemobankMandatePerCompanyStatus;

export const selectCompanyMemobankMandatePerCompany = (state: RootState) =>
  state.sepaDebit.companyMemobankMandate;

export const selectIsMemobankMandateSent = (state: RootState) =>
  state.sepaDebit.isMemobankMandateSent;

export const selectGetCompanySepaDebitInfoByAccountProviderIdStatus = (
  state: RootState,
) => state.sepaDebit.getCompanyByAccountProviderIdStatus;

export const selectCompanySepaDebitInfo = (state: RootState) =>
  state.sepaDebit.companySepaDebitInfo;

export const selectIsStripeConnectMandateSigned = (state: RootState) =>
  state.sepaDebit.isStripeConnectMandateSigned;

export const selectIsStripePlatformMandateSigned = (state: RootState) =>
  state.sepaDebit.isStripePlatformMandateSigned;

export default sepaDebitSlice.reducer;
